/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import { ContactFilterAtom } from "../../../atoms/ContactFilterAtom";
import {
  ClearFilterAtom,
  FilterCounterAtom,
} from "../../../atoms/ContactFilterAtoms";
import { IOption } from "../../../types/CommonTypes";
import { IRecipientTag } from "../../../types/SavedSearchTypes";
import { ITagQuery } from "../../../types/TagQueryTypes";
import { ITag } from "../../../types/TagTypes";
import { recipientToTagQuery } from "../../../utils/TagQuery";
import { useModal } from "../../Modal/Modal";
import SavedSearchesModal from "../../NewEmail/Modals/SavedSearches";
import TagSelectRecipient from "../../TagSelect/TagSelectRecipient";
import {
  AddCriteriaButton,
  LogicButton,
  LogicButtons,
  LogicButtonsWrapper,
} from "./SidebarStyles";

export const NewRecipientTag = (): IRecipientTag => ({
  option: {
    name: "All contacts",
    value: "ALL",
  },
  logic: { value: "AND", name: "And" },
  tags: [],
  id: uuidv4(),
});

const Container = styled.div`
  margin-top: 20px;
`;

export default function FilterByTags() {
  const setFilterCounter = useSetRecoilState(FilterCounterAtom);
  const modal = useModal();

  const [contactFilter, setContactFilter] = useRecoilState(ContactFilterAtom);
  const [resetTagSelect, setResetTagSelect] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const [recipientTags, setRecipientTags] = useState<IRecipientTag[]>(
    contactFilter?.recipientTags
      ? contactFilter.recipientTags
      : [NewRecipientTag()]
  );

  const shouldClear = useRecoilValue(ClearFilterAtom);

  useEffect(() => {
    if (shouldClear) setResetTagSelect(true);
  }, [shouldClear]);

  useEffect(() => {
    if (resetTagSelect) {
      setTimeout(() => {
        setResetTagSelect(false);
      }, 100);
    }
  }, [resetTagSelect]);

  useEffect(() => {
    if (resetTagSelect) {
      setRecipientTags([NewRecipientTag()]);
    }
  }, [resetTagSelect]);

  useEffect(() => {
    let byTagsCount =
      (contactFilter?.withTags.length > 0
        ? +contactFilter?.withTags.length
        : 0) +
      (contactFilter?.withoutTag.length > 0
        ? +contactFilter?.withoutTag.length
        : 0) +
      (contactFilter?.allOfTags.length > 0
        ? +contactFilter?.allOfTags.length
        : 0);

    setFilterCounter((oldCounts) => ({ ...oldCounts, byTagsCount }));
  }, [
    contactFilter?.withTags,
    contactFilter?.withoutTag,
    contactFilter?.allOfTags,
  ]);

  const lastSetRecipientTags = useRef<IRecipientTag[]>([]);
  useEffect(() => {
    const areRecipientTagsEqualToLast = isEqual(
      lastSetRecipientTags.current,
      contactFilter?.recipientTags
    );

    if (contactFilter?.recipientTags && !areRecipientTagsEqualToLast) {
      setRecipientTags(contactFilter.recipientTags);
      lastSetRecipientTags.current = contactFilter.recipientTags;
    }
  }, [contactFilter]);

  async function handleSaveRecipient() {
    let allOfTags: ITag[] = [];
    let withoutTag: ITag[] = [];
    let withTags: ITag[] = [];

    recipientTags.forEach((r) => {
      if (r.option.value === "ANY_OF") {
        withTags = [...r.tags, ...withTags];
      } else if (r.option.value === "ALL_OF") {
        allOfTags = [...r.tags, ...allOfTags];
      } else if (r.option.value === "NONE_OF") {
        withoutTag = [...r.tags, ...withoutTag];
      }
    });
    const tagQuery: ITagQuery = recipientToTagQuery(recipientTags);

    setContactFilter({
      withTags,
      withoutTag,
      allOfTags,
      tagQuery,
      recipientTags,
    });
  }

  async function handleSaveCreatedSearch() {
    const tagQuery: ITagQuery = recipientToTagQuery(recipientTags);

    if (tagQuery.tags.length === 0) {
      toast("There must be at least one tag", { type: "info" });
      return;
    }

    modal.pushModal(<SavedSearchesModal tagQuery={tagQuery} />, "", true);
  }

  async function addRecipientTags() {
    setRecipientTags([...recipientTags, NewRecipientTag()]);
  }

  const handleOptionRecipientTags = (option: IOption, index: number) => {
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.option = { value: option.value, name: option.name };
    items[index] = item;

    if (option.value === "ALL") {
      item.tags = [];
    }

    setRecipientTags(items);
  };

  function handleLogicRecipientTags(logic: IOption, index: number) {
    const items = [...recipientTags];
    const item = { ...items[index], logic };
    items[index] = item;

    setRecipientTags(items);
  }

  function handleTagsRecipientTags(tags: ITag[], index: number) {
    isFirstRender && setIsFirstRender(false);
    const items = [...recipientTags];
    const item = { ...items[index] };
    item.tags = tags;
    items[index] = item;
    setRecipientTags(items);
  }

  useEffect(() => {
    if (isFirstRender) {
      return;
    } else {
      handleSaveRecipient();
    }
  }, [recipientTags]);

  function removeRecipientTags(currentTag: IRecipientTag) {
    setRecipientTags(
      recipientTags.filter((recipientTag) => {
        return recipientTag.id !== currentTag.id;
      })
    );
  }

  //If I have more than one tag criteria, then I go to the first one and change it
  //to "All Contacts", all the other ones goes bye bye
  useEffect(() => {
    if (recipientTags.length === 1) return;
    const firstOne = recipientTags[0];

    if (firstOne.option.value === "ALL") {
      setRecipientTags([firstOne]);
    }
  }, [recipientTags]);

  return (
    <Container>
      {recipientTags.map((recipientTag, index) => (
        <div
          css={css`
            margin: 0 auto;
          `}
        >
          <TagSelectRecipient
            key={`recipients_recipient_tag_${recipientTag.id}`}
            recipientTag={recipientTag}
            index={index}
            removeRecipientTags={removeRecipientTags}
            handleOptionRecipientTags={handleOptionRecipientTags}
            handleTagsRecipientTags={handleTagsRecipientTags}
            isFilterContact
            resetTagSelect={resetTagSelect}
          />
          {recipientTags[index + 1] && (
            <LogicButtonsWrapper>
              <LogicButtons>
                <LogicButton
                  css={css`
                    border-radius: 4px 0px 0px 4px;
                    border-right: 1px solid #ffffff;
                  `}
                  isActive={recipientTag.logic.name === "And"}
                  onClick={() =>
                    handleLogicRecipientTags(
                      { name: "And", value: "AND" },
                      index
                    )
                  }
                >
                  And
                </LogicButton>
                <LogicButton
                  css={css`
                    border-radius: 0px 4px 4px 0px;
                    border-left: 1px solid #ffffff;
                  `}
                  isActive={recipientTag.logic.name === "Or"}
                  onClick={() =>
                    handleLogicRecipientTags({ name: "Or", value: "OR" }, index)
                  }
                >
                  Or
                </LogicButton>
              </LogicButtons>
            </LogicButtonsWrapper>
          )}
        </div>
      ))}

      {recipientTags[0].option.value !== "ALL" && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <AddCriteriaButton onClick={addRecipientTags}>
            + Add criteria
          </AddCriteriaButton>
          <AddCriteriaButton
            style={{ textAlign: "right" }}
            onClick={handleSaveCreatedSearch}
          >
            Save Search
          </AddCriteriaButton>
        </div>
      )}
    </Container>
  );
}
